/**
 * OathTableComponents - Shared table UI components
 * Reusable components extracted from Power Inquiry for use across all data tables.
 * Includes form controls (checkbox, radio), animation, table helpers (sortable header,
 * pagination, selection banner), and display utility functions.
 */

import {
  CheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// FORM CONTROLS
// =============================================================================

/**
 * Custom Checkbox component
 * When onChange is provided, renders as interactive button with stopPropagation.
 * When onChange is omitted, renders as display-only div (for use inside larger clickable elements).
 *
 * @param {boolean} checked - Whether checkbox is checked
 * @param {function} [onChange] - Change handler: (newChecked: boolean) => void
 * @param {'sm'|'md'|'lg'} [size='md'] - Size variant
 *   - sm: 16px (w-4 h-4) — compact contexts
 *   - md: 18px (w-[18px] h-[18px]) — default for filter panels, table rows
 *   - lg: 20px with QHD/4K scaling — table header "select all"
 */
export const OathCheckbox = ({ checked, onChange, size = 'md' }) => {
  const sizes = {
    sm: { box: 'w-4 h-4', icon: 'h-2.5 w-2.5' },
    md: { box: 'w-[18px] h-[18px]', icon: 'h-3 w-3' },
    lg: { box: 'w-5 h-5 qhd:w-6 qhd:h-6', icon: 'h-3.5 w-3.5 qhd:h-4 qhd:w-4' },
  };
  const { box, icon } = sizes[size] || sizes.md;

  const className = `relative ${box} rounded-[4px] border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
    onChange ? 'cursor-pointer' : ''
  } ${
    checked
      ? 'border-primary bg-primary shadow-sm'
      : `border-gray-300 dark:border-gray-500 ${
          onChange
            ? 'hover:border-primary/60 dark:hover:border-primary-light/60'
            : 'group-hover:border-primary/60 dark:group-hover:border-primary-light/60'
        }`
  }`;

  const checkIcon = (
    <CheckIcon
      className={`${icon} text-white stroke-[3] transition-all duration-200 ${
        checked ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
      }`}
    />
  );

  if (onChange) {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={(e) => {
          e.stopPropagation();
          onChange(!checked);
        }}
        className={className}
      >
        {checkIcon}
      </button>
    );
  }

  return <div className={className}>{checkIcon}</div>;
};

/**
 * Custom Radio button component
 * Display-only (no onChange) — for use inside larger clickable elements like FilterItem.
 *
 * @param {boolean} checked - Whether radio is selected
 */
export const OathRadio = ({ checked }) => (
  <div
    className={`relative w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
      checked
        ? 'border-primary bg-primary shadow-sm'
        : 'border-gray-300 dark:border-gray-500 group-hover:border-primary/60 dark:group-hover:border-primary-light/60'
    }`}
  >
    <div
      className={`w-2 h-2 rounded-full bg-white transition-all duration-200 ${
        checked ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
      }`}
    />
  </div>
);

// =============================================================================
// ANIMATION
// =============================================================================

/**
 * Animated expand/collapse panel using CSS Grid trick.
 * Provides smooth height animation without needing to know content height.
 *
 * @param {boolean} open - Whether panel is expanded
 * @param {React.ReactNode} children - Panel content
 * @param {string} [className] - Additional classes for the inner overflow container
 */
export const OathAnimatedPanel = ({ open, children, className = '' }) => (
  <div
    className={`grid transition-all duration-200 ease-in-out ${
      open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
    }`}
  >
    <div className={`overflow-hidden ${className}`}>
      {children}
    </div>
  </div>
);

// =============================================================================
// TABLE COMPONENTS
// =============================================================================

/**
 * Sortable table column header with toggle icon.
 * Clicking calls onSort(field, newDescending) where newDescending toggles if already active,
 * or defaults to true (descending) for a newly-selected column.
 *
 * @param {string} label - Column display name
 * @param {string} field - Sort field identifier
 * @param {string} sortField - Currently active sort field
 * @param {boolean} sortDescending - Whether current sort is descending
 * @param {function} onSort - (field, descending) => void
 * @param {'left'|'center'|'right'} [align='left'] - Text alignment
 */
export const OathSortableHeader = ({ label, field, sortField, sortDescending, onSort, align = 'left', tooltip }) => {
  const isActive = sortField?.toLowerCase() === field?.toLowerCase();
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  const SortIcon = isActive
    ? (sortDescending ? ChevronDownIcon : ChevronUpIcon)
    : ChevronUpDownIcon;

  return (
    <button
      onClick={() => onSort(field, isActive ? !sortDescending : true)}
      title={tooltip}
      className={`flex items-center gap-1 qhd:gap-2 ${alignClass} w-full text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider transition-colors ${
        isActive
          ? 'text-primary dark:text-primary-light'
          : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light'
      }`}
    >
      {label}
      <SortIcon className={`h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 ${isActive ? 'text-primary dark:text-primary-light' : 'text-gray-400'}`} />
    </button>
  );
};

/**
 * Sentinel page size meaning "show all items". It's a real (large) number so it
 * stays safe in slice/ceil arithmetic in consumers. Include it in pageSizeOptions
 * to add an "All" choice; it renders as "All" in the selector. Opt-in only — omit
 * it for server-paginated tables where fetching everything would be expensive.
 */
export const PAGE_SIZE_ALL = Number.MAX_SAFE_INTEGER;

/**
 * Pagination component with page size selector, page navigation, and loading skeletons.
 * Uses 1-based page indexing.
 *
 * @param {number} currentPage - Current page (1-based)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalCount - Total number of items
 * @param {number} pageSize - Items per page
 * @param {function} onPageChange - (page: number) => void
 * @param {function} onPageSizeChange - (pageSize: number) => void
 * @param {boolean} [isLoading] - Disables controls while loading
 * @param {boolean} [isLoadingAggregates] - Shows skeleton placeholders for totals
 * @param {string} [label] - Optional noun after count (e.g. "reports")
 * @param {number[]} [pageSizeOptions] - Available page sizes; include PAGE_SIZE_ALL for an "All" option
 */
export const OathPagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  isLoadingAggregates = false,
  label,
  pageSizeOptions = [25, 50, 100, 200],
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount || startItem + pageSize - 1);

  const LoadingSkeleton = () => (
    <span className="inline-block w-12 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
  );

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 qhd:gap-6 px-5 py-4 qhd:px-8 qhd:py-5 4k:px-10 4k:py-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
      {/* Results info */}
      <div className="text-sm qhd:text-base 4k:text-lg text-gray-600 dark:text-gray-400">
        Showing <span className="font-semibold text-gray-900 dark:text-white">{startItem.toLocaleString()}</span>
        {' '}-{' '}
        <span className="font-semibold text-gray-900 dark:text-white">{endItem.toLocaleString()}</span>
        {' '}of{' '}
        {isLoadingAggregates ? (
          <LoadingSkeleton />
        ) : (
          <span className="font-semibold text-gray-900 dark:text-white">{totalCount.toLocaleString()}</span>
        )}
        {label && ` ${label}`}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-6 qhd:gap-8">
        {/* Page size selector */}
        <div className="flex items-center gap-2 qhd:gap-3">
          <span className="text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">Show:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            disabled={isLoading}
            className="px-3 py-1.5 qhd:px-4 qhd:py-2 4k:px-5 4k:py-2.5 text-sm qhd:text-base 4k:text-lg border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size === PAGE_SIZE_ALL ? 'All' : size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-1 qhd:gap-2">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage <= 1 || isLoading}
            className="p-2 qhd:p-2.5 4k:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="First page"
          >
            <ChevronDoubleLeftIcon className="h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 text-gray-600 dark:text-gray-400" />
          </button>

          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1 || isLoading}
            className="p-2 qhd:p-2.5 4k:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous page"
          >
            <ChevronLeftIcon className="h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 text-gray-600 dark:text-gray-400" />
          </button>

          <div className="px-4 py-1.5 qhd:px-5 qhd:py-2 4k:px-6 4k:py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
            <span className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white">
              {currentPage}
            </span>
            <span className="text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">
              {' / '}
              {isLoadingAggregates ? (
                <span className="inline-block w-6 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse align-middle" />
              ) : (
                totalPages
              )}
            </span>
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || isLoading}
            className="p-2 qhd:p-2.5 4k:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next page"
          >
            <ChevronRightIcon className="h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 text-gray-600 dark:text-gray-400" />
          </button>

          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage >= totalPages || isLoading}
            className="p-2 qhd:p-2.5 4k:p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Last page"
          >
            <ChevronDoubleRightIcon className="h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Selection banner — appears above table content when items are selected.
 *
 * Two modes determined by the presence of pageCount:
 * - With pageCount (ResultsTable mode): Shows only when ALL items on the current page are selected.
 *   Offers "Select all X matching" when there are more items beyond the page.
 * - Without pageCount (GroupedResultsTable mode): Shows whenever ANY items are selected.
 *   Shows count, "Select all" option, and "Clear selection" link.
 *
 * @param {number} selectedCount - Number of currently selected items
 * @param {number} totalCount - Total matching items across all pages
 * @param {number} [pageCount] - Number of items on the current page (enables page-aware mode)
 * @param {boolean} selectAllMatching - Whether "select all matching" is active
 * @param {function} onSelectAllMatching - (selectAll: boolean) => void
 * @param {function} onClearSelection - () => void
 */
export const OathSelectionBanner = ({
  selectedCount,
  totalCount,
  pageCount,
  selectAllMatching,
  onSelectAllMatching,
  onClearSelection,
  children,
}) => {
  if (selectedCount === 0) return null;

  const allPageSelected = pageCount !== undefined && pageCount > 0 && selectedCount >= pageCount;
  const hasMoreThanPage = pageCount !== undefined
    ? totalCount > pageCount
    : totalCount > selectedCount;

  return (
    <div className="bg-primary-50 dark:bg-primary-900/30 border-b border-primary-200 dark:border-primary-800 px-4 py-2.5 qhd:py-3 flex flex-wrap items-center justify-center gap-2 text-sm qhd:text-base">
      {selectAllMatching ? (
        <>
          <span className="text-primary-700 dark:text-primary-300">
            All <strong>{totalCount.toLocaleString()}</strong> powers matching your search are selected.
          </span>
          <button
            onClick={onClearSelection}
            className="text-primary dark:text-primary-light hover:underline font-medium"
          >
            Clear selection
          </button>
        </>
      ) : (
        <>
          <span className="text-primary-700 dark:text-primary-300">
            {allPageSelected ? (
              <>All <strong>{selectedCount.toLocaleString()}</strong> powers on this page are selected.</>
            ) : (
              <><strong>{selectedCount.toLocaleString()}</strong> power{selectedCount !== 1 ? 's' : ''} selected.</>
            )}
          </span>
          {allPageSelected && hasMoreThanPage && (
            <button
              onClick={() => onSelectAllMatching(true)}
              className="text-primary dark:text-primary-light hover:underline font-medium"
            >
              Select all {totalCount.toLocaleString()} powers matching your search
            </button>
          )}
          <span className="text-primary-400 dark:text-primary-500">|</span>
          <button
            onClick={onClearSelection}
            className="text-primary dark:text-primary-light hover:underline font-medium"
          >
            Clear selection
          </button>
        </>
      )}
      {children}
    </div>
  );
};

// =============================================================================
// DISPLAY UTILITIES
// =============================================================================

// Cached formatter instance for performance
const _currencyDisplayFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

/**
 * Format currency for table display (with $ symbol, '-' for null/undefined)
 * @param {number|null|undefined} value - Amount to format
 * @returns {string} Formatted currency string (e.g. "$25,000.00") or '-'
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return _currencyDisplayFormatter.format(value);
};

/**
 * Format date for table display (MM/DD/YYYY, '-' for null/undefined)
 * @param {string|null|undefined} dateString - ISO date string
 * @returns {string} Formatted date string (e.g. "01/15/2025") or '-'
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Power status color map for badge styling.
 * Returns Tailwind classes for background, text color, and dark mode variants.
 *
 * @param {string} status - Power status code (E, U, D, F, J, V, R)
 * @returns {string} Tailwind CSS classes for the status badge
 */
export const getPowerStatusColor = (status) => {
  const statusColors = {
    'E': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',     // Executed
    'U': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',             // Unexecuted
    'D': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',          // Discharged
    'F': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',  // Forfeiture
    'J': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',              // Judgment
    'V': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',  // Void
    'R': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',  // Recalled
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};
